import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getCurrentBrowserAuthHeaders,
  getStoredEmployeeEmail,
  getCurrentClientRouteClientId,
} from "@/lib/api";
import { trackPageView } from "@/services/activityLogService";

const DEDUPE_WINDOW_MS = 1500;

function isTrackablePage(pathname: string): boolean {
  if (
    pathname.startsWith("/prompt-lab") ||
    pathname.startsWith("/fdr") ||
    pathname.startsWith("/feuille-de-route-ia")
  ) {
    return false;
  }

  if (pathname.startsWith("/admin")) {
    return true;
  }

  if (pathname === "/pm/dashboard" || pathname.startsWith("/pm/board/")) {
    return true;
  }

  return getCurrentClientRouteClientId(pathname) !== null;
}

export default function AppActivityTracker() {
  const location = useLocation();
  const lastTrackedRef = useRef<{ key: string; at: number } | null>(null);

  useEffect(() => {
    if (!isTrackablePage(location.pathname)) {
      return;
    }

    const authHeaders = getCurrentBrowserAuthHeaders({ pathname: location.pathname });
    const hasIdentifiedActor = Boolean(
      getStoredEmployeeEmail() || authHeaders["x-client-user-email"],
    );

    if (!hasIdentifiedActor) {
      return;
    }

    const dedupeKey = `${location.pathname}${location.search}`;
    const now = Date.now();

    if (
      lastTrackedRef.current?.key === dedupeKey &&
      now - lastTrackedRef.current.at < DEDUPE_WINDOW_MS
    ) {
      return;
    }

    lastTrackedRef.current = { key: dedupeKey, at: now };

    void trackPageView({
      app_area: location.pathname.startsWith("/admin") ? "admin" : undefined,
      client_id: authHeaders["x-client-id"] ?? null,
      route_path: location.pathname,
      metadata: {
        search: location.search || "",
      },
    });
  }, [location.key, location.pathname, location.search]);

  return null;
}
